import { useMemo } from 'react'
import { FormattedMessage, FormattedDate } from 'react-intl'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import { filter, flatMap, get, find, size } from 'lodash'
import PageSection from '../../../shared/components/layouts/PageSection'
import FormattedBytes from '../../../shared/components/intl/FormattedBytes'
import { useProjects } from '../../../contexts/ProjectsContext'
import { useServices } from '../../../contexts/ServicesContext'

function NodeFactCard({ labelId, value }) {
  return (
    <Box sx={{ p: 2, border: 1, borderColor: 'divider', borderRadius: 1 }}>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
        <FormattedMessage id={labelId} />
      </Typography>
      <Typography variant="h6">
        {value ?? <FormattedMessage id="nodes.facts.unknown" />}
      </Typography>
    </Box>
  )
}

function NodeGeneralTab({ node, addresses = [] }) {
  const { projects } = useProjects()
  const { servicesByProjectId } = useServices()

  const publicIp = useMemo(() => {
    const publicAddr = find(addresses, (addr) => addr.isPublic && addr.addressType === 'ipv4')
    return publicAddr ? publicAddr.address : null
  }, [addresses])

  const nodeProjects = useMemo(() => {
    return filter(projects, (project) => {
      const nodeIds = get(project, 'nodeIds', [])
      return nodeIds.includes(node.id)
    })
  }, [projects, node.id])

  const projectCount = size(nodeProjects)

  const serviceCount = useMemo(() => {
    return size(flatMap(nodeProjects, (project) => get(servicesByProjectId, project.id, [])))
  }, [nodeProjects, servicesByProjectId])

  const cpuCount = get(node, 'latestStatus.cpuCount')
  const memoryBytes = get(node, 'latestStatus.memoryInBytes')
  const diskBytes = get(node, 'latestStatus.diskInBytes')

  return (
    <PageSection titleId="nodes.sections.general">
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' },
          gap: 2,
        }}
      >
        <NodeFactCard
          labelId="nodes.facts.cpuCount"
          value={cpuCount}
        />
        <NodeFactCard
          labelId="nodes.facts.ram"
          value={memoryBytes ? <FormattedBytes value={memoryBytes} /> : null}
        />
        <NodeFactCard
          labelId="nodes.facts.disk"
          value={diskBytes ? <FormattedBytes value={diskBytes} /> : null}
        />
        <NodeFactCard
          labelId="nodes.facts.publicIp"
          value={publicIp}
        />
        <NodeFactCard
          labelId="nodes.facts.lastSeen"
          value={
            node.lastSeenAt ? (
              <FormattedDate
                value={node.lastSeenAt}
                year="numeric"
                month="long"
                day="numeric"
                hour="numeric"
                minute="numeric"
              />
            ) : null
          }
        />
        <NodeFactCard
          labelId="nodes.facts.projects"
          value={projectCount}
        />
        <NodeFactCard
          labelId="nodes.facts.services"
          value={serviceCount}
        />
      </Box>
    </PageSection>
  )
}

export default NodeGeneralTab
